/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {
  Component,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {ConfirmModalComponent} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.component';
import {AlertNotificationSeverity} from '../../alert-notification-severity.model';
import {ConfirmModal} from '../../shared/confirm-modal/deprecated-modals/confirm-modal.model';
import {Router} from '@angular/router';
import {LoadingService} from '../../shared/loading/loading-service/loading.service';
import {Subscription} from 'rxjs';
import {ClaimCommandModel} from '../../api/ms-claims/contracts/claim-command.model';
import {WizardService} from '../../shared/wizard/wizard.service';
import {ClaimInformationDataModel} from '../models/claim-information-data.model';
import {ClaimsApi} from '../../api/ms-claims/claims.api';
import {ClaimPositionCommandModel} from '../../api/ms-claims/contracts/claim-position-command.model';
import {ClaimPositionDataModel} from '../models/claim-position-data.model';
import {
    delay,
    startWith
} from 'rxjs/operators';
import {WizardStepModel} from '../../shared/wizard/wizard-step.model';
import {ClaimSendHelperService} from '../services/claim-send-helper.service';
import {ToastWrapperService} from '../../shared/utils/toast-wrapper-service/toast-wrapper-service';
import {FileManagerApi} from '../../api/ms-filemanager/filemanager.api';
import {UnsavedChangesService} from '../../shared/guards/unsaved-work/unsaved-changes.service';
import {ReplayableRouterEventsService} from '../../shared/services/replayable-router-events.service';
import {UserService} from '../../shared/services/user.service';
import {Permission} from '../../shared/utils/permission.enum';
import {toBase64Utf16LittleEndian} from '../../shared/utils/encode/encodeToBase64Utf16LE';

@Component({
    selector: 'app-claim-create-new',
    templateUrl: './claim-create-new.component.html',
    styleUrls: ['./claim-create-new.component.css'],
    providers: [WizardService],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ClaimCreateNewComponent implements OnInit, OnDestroy {
    public canSave = false;
    public canSend = false;
    public MODAL_TITLE = 'Claim yet';
    public isExited = false;
    private hasSubmitted = false;
    private subscriptions = new Subscription();

    /*
     * TODO : Create a shared file w/ const values across the application
     */

    constructor(private router: Router,
                private wizard: WizardService,
                private modalService: NgbModal,
                private toast: ToastWrapperService,
                private loading: LoadingService,
                private claimsApi: ClaimsApi,
                private claimSendHelper: ClaimSendHelperService,
                private fileManagerApi: FileManagerApi,
                private unsavedChangesService: UnsavedChangesService,
                private replay: ReplayableRouterEventsService,
                private userService: UserService) {
    }

    ngOnInit(): void {
        this.subscriptions.add(
                this.wizard.observable.pipe(
                        startWith({canSave: false, canSend: false} as WizardStepModel),
                        delay(0)
                )
                        .subscribe((value) => {
                            this.canSave = value.canSave;
                            this.canSend = value.canSend;
                        }));

        this.unsavedChangesService.originalValue = {} as ClaimInformationDataModel;
        this.subscriptions.add(
                this.replay.onGuardsCheckStart.subscribe(() => {
                    this.updateCurrentState();
                })
        );
    }

    ngOnDestroy(): void {
        const claimInformation = this.wizard.steps.get(0).data as ClaimInformationDataModel;

        if (this.userService.hasPermissions([
            Permission.PARTNER_DIRECTORY_CREATE,
            Permission.PARTNER_FILE_UPLOAD
        ])) {
            if (!this.hasSubmitted && claimInformation.attachmentDirectory != null) {
                this.fileManagerApi.deleteDirectory(claimInformation.attachmentDirectory)
                        .subscribe();
            }
        }

        this.subscriptions.unsubscribe();
    }

    sendClaim(): void {
        this.isExited = true;
        this.hasSubmitted = true;
        this.claimSendHelper.createAndSendClaim(this.createClaim());
    }

    saveClaim(): void {
        const confirmModalInstance: ConfirmModalComponent = this.modalService.open(ConfirmModalComponent, {
            centered: true
        }).componentInstance;
        confirmModalInstance.initModal({
            titleText: 'Confirm',
            subtitleText: 'Do you want to save the new Claim as draft?',
            alertNotificationSeverity: AlertNotificationSeverity.INFORMATION,
            primaryButtonText: 'Save',
            secondaryButtonText: 'Cancel'
        } as ConfirmModal)
                .confirmEvent
                .subscribe(() => {
                    /** Start spinner loading*/
                    this.loading.start();
                    this.isExited = true;
                    this.hasSubmitted = true;
                    this.subscriptions.add(this.claimsApi.createClaim(this.createClaim())
                            .subscribe(data => {
                                /** Stop spinner loading*/
                                this.loading.stop();
                                this.toast.success(`Claim No.<span class="toast-new-claim-number">${data.number}</span>`
                                        + `<strong> saved</strong> successfully`,
                                        'Success');
                                this.router.navigate(['/', 'claim']);
                            }));
                });
    }

    closeWizard(): void {
        this.router.navigate(['/', 'claim']);
    }

    createClaim(): ClaimCommandModel {
        const claimInformation = this.wizard.steps.get(0).data as ClaimInformationDataModel;
        const claimMaterialPositions = this.wizard.steps.get(1).data as ClaimPositionDataModel[];

        const claimPositions: ClaimPositionCommandModel[] = claimMaterialPositions.map(position => ({
            quantity: position.quantity ? Number(position.quantity) : null,
            decisionCode: position.decision ? position.decision.code : null,
            claimErrorCode: position.claimError ? position.claimError.code : null,
            handlingUnit: position.handlingUnit,
            timeEffort: position.timeEffort != null ? Number(position.timeEffort) : null,
            businessCategoryCode: position.businessCategory ? position.businessCategory.code : null
        }));

        return {
            claimCode: claimInformation.claimCode.code,
            description: toBase64Utf16LittleEndian(claimInformation.description),
            lotNumber: claimInformation.lotNumber,
            materialNumber: claimInformation.material.number,
            mcdrNumber: claimInformation.mcdrNumber,
            modelCode: claimInformation.modelCode,
            modelType: claimInformation.modelType,
            partnerCode: claimInformation.partner.code,
            subject: claimInformation.subject,
            spoNumber: claimInformation.spoNumber,
            supplyingPackingPlantCode: claimInformation.supplyingPackingPlant.code,
            claimPositions: claimPositions,
            attachmentDirectory: claimInformation.attachmentDirectory
        };
    }

    private updateCurrentState(): void {
        const headerStep = this.wizard.steps.get(0);
        if (headerStep) {
            const currentState = {...headerStep.data};
            delete currentState.attachmentDirectory;
            this.unsavedChangesService.currentValue = currentState;
        }
    }

    private stepIsFilled(): boolean {
        const data = this.wizard.steps.get(0).data;

        return data.partner != null
                || data.claimCode != null
                || data.description != null
                || data.lotNumber != null
                || data.material != null
                || data.mcdrNumber != null
                || data.modelCode != null
                || data.modelType != null
                || data.subject != null
                || data.supplyingPackingPlant != null;
    }
}
